import React, { useEffect, useState } from 'react';
import { api, API_BASE_URL } from '../api/client';
import { useNavigate, useLocation } from 'react-router-dom';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get('error');
    if (err === 'google_oauth_unavailable') {
      setMessage('Google sign-in is not configured. Please use email/password or contact support.');
    } else if (err === 'google_oauth_failed') {
      setMessage('Google sign-in failed. Please try again or use email/password.');
    }
    
    const reason = params.get('reason');
    if (reason === 'expired') {
      setMessage('Session expired. Please log in again.');
    }
  }, [location.search]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post(`/login`, { email: email.trim(), password });
      const { token, userId, is_admin } = response.data;
      if (token) {
        const setAuthData = () => {
          localStorage.setItem('token', token);
          localStorage.setItem('userId', String(userId));
          localStorage.setItem('is_admin', String(is_admin));
        };

        try {
          setAuthData();
        } catch (e: any) {
          if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
            // Storage full, try to clean up old session data
            try {
              const keysToRemove: string[] = [];
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (
                  key.startsWith('sessionStats:') ||
                  key.startsWith('sessionMonitor:') ||
                  key.startsWith('sessionEvents:') ||
                  key.startsWith('sessionTimes:') ||
                  key.startsWith('soundEndPlayed:')
                )) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach(key => localStorage.removeItem(key));
              
              // Retry setting data
              setAuthData();
            } catch (retryErr) {
              console.error('Storage cleanup failed or insufficient:', retryErr);
              setMessage('Login failed: Local storage is full. Please clear your browser cache.');
              return;
            }
          } else {
            throw e;
          }
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Dispatch custom event to notify App component about login state change
        window.dispatchEvent(new Event('auth-change'));
        
        let dest = '/schedule';
        try {
          const usage = await api.get(`/users/${userId}/usage`);
          const tier = usage.data?.tier;
          if (typeof tier === 'string') {
            try {
              localStorage.setItem('userTier', tier);
            } catch {}
          }
        } catch {}
        
        navigate(dest);
      }
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setMessage('Server request timed out. Please try again.');
      } else if (!error.response) {
        setMessage(error.message || 'Network error. Please check your connection.');
      } else {
        setMessage(error.response?.data?.message || 'Login failed');
      }
    }
  };

  const handleRegister = () => {
    navigate('/register');
  };

  return (
    <div className="container mt-5">
      <h2>Login</h2>
      <form onSubmit={handleLogin}>
        <div className="mb-3">
          <label htmlFor="emailInput" className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            id="emailInput"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="passwordInput" className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            id="passwordInput"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <a href="/forgot-password" className="text-decoration-none">Forgot password?</a>
        </div>
        <button type="submit" className="btn btn-primary">Login</button>
      </form>
      <div className="mt-4">
        <p>Or continue with:</p>
        <a href={`${API_BASE_URL}/auth/google`} className="btn btn-outline-danger me-2">Google</a>
        <button type="button" className="btn btn-outline-primary" onClick={handleRegister}>Create account</button>
      </div>
      {message && <p className="mt-3 text-danger" role="alert" aria-live="assertive">{message}</p>}
    </div>
  );
};

export default Login;
